import { useRef, useState } from "react";
import { Crown, Upload, X, Loader2, Check, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCorporate } from "./CorporateLayout";

export default function CorporateBranding() {
  const { corp, reloadCorp } = useCorporate();
  const fileRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const notify = () => {
    window.dispatchEvent(new CustomEvent("logerd:corporate-updated"));
    reloadCorp();
  };

  const saveName = async (e) => {
    e?.preventDefault();
    const trimmed = nameDraft.trim();
    if (!trimmed) { toast.error("Corporate name can't be empty."); return; }
    if (trimmed === corp?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await api.put("/corporate/me", { name: trimmed });
      notify();
      toast.success("Corporate name updated");
      setEditingName(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rename failed");
    } finally {
      setSavingName(false);
    }
  };

  const onLogoFile = async (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    if (f.size > 600_000) { toast.error("Please pick a smaller image (under 500 KB)."); return; }
    if (!/^image\//.test(f.type)) { toast.error("Logo must be an image file."); return; }
    setLogoBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error("read failed"));
        r.readAsDataURL(f);
      });
      await api.put("/corporate/me", { logo_data_url: dataUrl });
      notify();
      toast.success("Corporate logo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Logo upload failed");
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.put("/corporate/me", { logo_data_url: "" });
      notify();
      toast.success("Corporate logo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <main className="px-6 md:px-10 py-8 max-w-4xl" data-testid="corporate-logo-section">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">District branding</h1>
        <p className="text-sm text-slate-600">The name and logo that appear on every location's kiosk and PDF reports.</p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm mb-6" data-testid="corporate-name-card">
        <div className="font-display text-lg font-semibold text-slate-900 mb-1">Corporate name</div>
        <div className="text-sm text-slate-600 mb-4">Shown across the console and district messages.</div>
        {editingName ? (
          <form onSubmit={saveName} className="flex items-center gap-2" data-testid="corporate-name-editor">
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setEditingName(false); }}
              maxLength={120}
              data-testid="corporate-name-input"
              className="font-display text-xl font-bold tracking-tight text-slate-900 bg-white border-2 border-primary rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-4 focus:ring-primary/20 min-w-[280px]"
            />
            <button type="submit" disabled={savingName} data-testid="corporate-name-save" className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-primary text-white hover:shadow disabled:opacity-60 transition">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </button>
            <button type="button" onClick={() => setEditingName(false)} data-testid="corporate-name-cancel" className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 transition">
              <X className="h-4 w-4" />
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <div className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="branding-name-display">{corp?.name || "…"}</div>
            <button
              type="button"
              onClick={() => { setNameDraft(corp?.name || ""); setEditingName(true); }}
              data-testid="corporate-name-edit"
              title="Rename corporate"
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm" data-testid="corporate-logo-card">
        <div className="flex items-start justify-between gap-6 flex-col sm:flex-row">
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-slate-900 mb-1">Corporate logo</div>
            <div className="text-sm text-slate-600 mb-3">
              Used as the default logo on every location's kiosk and PDF reports. Each location can still upload its own logo in its Settings, which will override the corporate one.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="corporate-logo-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={logoBusy} data-testid="corporate-logo-upload" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition disabled:opacity-60">
                {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {corp?.logo_data_url ? "Replace logo" : "Upload logo"}
              </button>
              {corp?.logo_data_url && (
                <button type="button" onClick={removeLogo} disabled={logoBusy} data-testid="corporate-logo-remove" className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 transition disabled:opacity-60">
                  <X className="h-4 w-4" /> Remove
                </button>
              )}
              <span className="text-xs text-slate-500">PNG or JPG · under 500 KB · square works best</span>
            </div>
          </div>
          <div className="flex items-center justify-center h-24 w-24 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex-shrink-0">
            <img src={corp?.logo_data_url || "/logerd-logo.png"} alt="Corporate logo" className={`h-full w-full object-contain ${corp?.logo_data_url ? "" : "opacity-70"}`} data-testid="corporate-logo-preview" />
          </div>
        </div>
      </section>
    </main>
  );
}
